import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  ArrowLeft,
  Building2,
  Wrench,
  MessageSquareText,
  Paperclip,
  Download,
  MapPin,
  Hash,
  Users,
  Calendar,
  UserCircle2,
  Euro,
  FileText,
} from "lucide-react";
import { api, API, formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";

const SERVICO_STATE_STYLES = {
  Agendado: "bg-blue-50 text-blue-700 border-blue-200",
  "Em Curso": "bg-amber-50 text-amber-700 border-amber-200",
  Concluído: "bg-emerald-50 text-emerald-700 border-emerald-200",
  Cancelado: "bg-zinc-100 text-zinc-500 border-zinc-200",
};

const CONSULTA_STATE_STYLES = {
  Pendente: "bg-amber-50 text-amber-700 border-amber-200",
  Realizada: "bg-emerald-50 text-emerald-700 border-emerald-200",
  Cancelada: "bg-zinc-100 text-zinc-500 border-zinc-200",
};

function formatDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("pt-PT", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function formatSize(bytes) {
  if (!bytes && bytes !== 0) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatEur(v) {
  const n = Number(v);
  if (!isFinite(n)) return "—";
  return n.toLocaleString("pt-PT", { style: "currency", currency: "EUR" });
}

export default function CondominioDetail() {
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("all"); // all | servicos | consultas

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const r = await api.get(`/condominios/${id}/timeline`);
        if (alive) setData(r.data);
      } catch (e) {
        toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const events = useMemo(() => {
    if (!data) return [];
    const list = [];
    if (filter === "all" || filter === "servicos") {
      for (const s of data.servicos || []) {
        list.push({ kind: "servico", date: s.data || s.created_at, item: s });
      }
    }
    if (filter === "all" || filter === "consultas") {
      for (const c of data.consultas || []) {
        list.push({ kind: "consulta", date: c.data || c.created_at, item: c });
      }
    }
    list.sort((a, b) => (a.date < b.date ? 1 : a.date > b.date ? -1 : 0));
    return list;
  }, [data, filter]);

  const totals = useMemo(() => {
    if (!data) return { servicos: 0, consultas: 0, anexos: 0 };
    const anexos = (data.servicos || []).reduce(
      (acc, s) => acc + (s.attachments?.length || 0),
      0
    );
    return {
      servicos: data.servicos?.length || 0,
      consultas: data.consultas?.length || 0,
      anexos,
    };
  }, [data]);

  function downloadAttachment(att) {
    const token = localStorage.getItem("crm_token");
    const url = `${API}/attachments/${att.id}/download?auth=${encodeURIComponent(token || "")}`;
    window.open(url, "_blank");
  }

  if (loading) {
    return (
      <div className="py-14 text-center text-sm font-manrope text-neutral-500">
        A carregar detalhe do condomínio…
      </div>
    );
  }

  if (!data) {
    return (
      <div className="py-14 text-center text-sm font-manrope text-neutral-500">
        Não foi possível carregar o condomínio.
      </div>
    );
  }

  const c = data.condominio;

  return (
    <div className="space-y-8" data-testid="condominio-detail-page">
      {/* Back link */}
      <Link
        to="/condominios"
        data-testid="condominio-detail-back"
        className="inline-flex items-center gap-2 text-sm font-manrope text-neutral-500 hover:text-[#0047FF] transition-colors"
      >
        <ArrowLeft className="w-4 h-4" strokeWidth={2} />
        Voltar aos condomínios
      </Link>

      {/* Header */}
      <div className="bg-white border border-zinc-200 rounded-sm p-8">
        <div className="flex items-start justify-between gap-6 flex-wrap">
          <div className="flex items-start gap-5">
            <div className="w-12 h-12 bg-[#0047FF]/10 border border-[#0047FF]/20 rounded-sm flex items-center justify-center shrink-0">
              <Building2 className="w-6 h-6 text-[#0047FF]" strokeWidth={1.75} />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.24em] text-neutral-400 font-chivo font-bold mb-2">
                Ficha do condomínio
              </div>
              <h1
                data-testid="condominio-detail-nome"
                className="text-3xl sm:text-4xl font-chivo font-black text-neutral-950 tracking-tight leading-tight"
              >
                {c.nome}
              </h1>
              <div className="mt-3 flex flex-wrap gap-x-6 gap-y-2 text-sm font-manrope text-neutral-600">
                <span className="inline-flex items-center gap-1.5">
                  <Hash className="w-3.5 h-3.5 text-neutral-400" strokeWidth={2} />
                  <span className="font-mono">{c.nif}</span>
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <MapPin className="w-3.5 h-3.5 text-neutral-400" strokeWidth={2} />
                  {c.morada}
                  {c.localidade ? `, ${c.localidade}` : ""}
                  {c.codigo_postal ? ` · ${c.codigo_postal}` : ""}
                </span>
                {c.empresa_condominio_nome && (
                  <span className="inline-flex items-center gap-1.5">
                    <Users className="w-3.5 h-3.5 text-neutral-400" strokeWidth={2} />
                    {c.empresa_condominio_nome}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        {c.notas && (
          <div className="mt-6 pt-6 border-t border-zinc-100">
            <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-400 font-chivo font-bold mb-2">
              Notas
            </div>
            <p className="text-sm font-manrope text-neutral-700 leading-relaxed whitespace-pre-line">
              {c.notas}
            </p>
          </div>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <StatCard label="Serviços" value={totals.servicos} icon={Wrench} testid="stat-servicos" />
        <StatCard
          label="Consultas"
          value={totals.consultas}
          icon={MessageSquareText}
          testid="stat-consultas"
        />
        <StatCard label="Anexos" value={totals.anexos} icon={Paperclip} testid="stat-anexos" />
      </div>

      {/* Filter tabs */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.24em] text-neutral-400 font-chivo font-bold mb-1">
            Cronologia
          </div>
          <h2 className="text-xl font-chivo font-black text-neutral-950 tracking-tight">
            Histórico do edifício
          </h2>
        </div>
        <div className="inline-flex items-center gap-1 bg-zinc-100 rounded-sm p-1">
          {[
            { k: "all", label: "Tudo" },
            { k: "servicos", label: "Serviços" },
            { k: "consultas", label: "Consultas" },
          ].map((t) => (
            <button
              key={t.k}
              data-testid={`filter-${t.k}`}
              onClick={() => setFilter(t.k)}
              className={`px-4 py-1.5 text-xs font-manrope font-semibold rounded-sm transition-colors ${
                filter === t.k
                  ? "bg-white text-neutral-950 shadow-sm"
                  : "text-neutral-500 hover:text-neutral-900"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {/* Timeline */}
      {events.length === 0 ? (
        <div className="bg-white border border-zinc-200 rounded-sm p-14 text-center">
          <FileText className="w-8 h-8 text-neutral-300 mx-auto mb-4" strokeWidth={1.5} />
          <div className="text-sm font-manrope text-neutral-500">
            Sem registos para mostrar.
          </div>
        </div>
      ) : (
        <ol className="relative border-l-2 border-zinc-200 ml-3 space-y-6" data-testid="timeline">
          {events.map((ev, idx) => (
            <li key={`${ev.kind}-${ev.item.id}-${idx}`} className="pl-8 relative">
              <span
                className={`absolute -left-[9px] top-1.5 w-4 h-4 rounded-full border-2 border-white ring-2 ${
                  ev.kind === "servico" ? "bg-[#0047FF] ring-[#0047FF]/20" : "bg-amber-500 ring-amber-200"
                }`}
              />
              {ev.kind === "servico" ? (
                <ServicoEvent servico={ev.item} onDownload={downloadAttachment} />
              ) : (
                <ConsultaEvent consulta={ev.item} />
              )}
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

function StatCard({ label, value, icon: Icon, testid }) {
  return (
    <div
      className="bg-white border border-zinc-200 rounded-sm p-5 flex items-center gap-4"
      data-testid={testid}
    >
      <div className="w-10 h-10 bg-zinc-50 border border-zinc-200 rounded-sm flex items-center justify-center shrink-0">
        <Icon className="w-4 h-4 text-neutral-700" strokeWidth={1.75} />
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 font-chivo font-bold">
          {label}
        </div>
        <div className="text-2xl font-chivo font-black text-neutral-950 leading-none mt-1">
          {value}
        </div>
      </div>
    </div>
  );
}

function ServicoEvent({ servico, onDownload }) {
  const stateStyle = SERVICO_STATE_STYLES[servico.estado] || "bg-zinc-100 text-zinc-600 border-zinc-200";
  return (
    <div
      className="bg-white border border-zinc-200 rounded-sm p-5 hover:border-zinc-300 transition-colors"
      data-testid={`event-servico-${servico.id}`}
    >
      <div className="flex items-start justify-between gap-4 flex-wrap mb-3">
        <div className="flex items-center gap-2 min-w-0">
          <Wrench className="w-4 h-4 text-[#0047FF] shrink-0" strokeWidth={2} />
          <div className="text-[10px] uppercase tracking-[0.24em] text-[#0047FF] font-chivo font-bold">
            Serviço
          </div>
          <div className="text-sm font-manrope font-semibold text-neutral-950 truncate">
            · {servico.tipo}
          </div>
        </div>
        <span
          className={`inline-flex items-center px-2.5 py-1 text-[10px] uppercase tracking-[0.16em] font-chivo font-bold border rounded-sm ${stateStyle}`}
        >
          {servico.estado}
        </span>
      </div>

      {servico.descricao && (
        <p className="text-sm font-manrope text-neutral-700 leading-relaxed mb-3 whitespace-pre-line">
          {servico.descricao}
        </p>
      )}

      <div className="flex flex-wrap gap-x-5 gap-y-1.5 text-xs font-manrope text-neutral-500">
        <span className="inline-flex items-center gap-1.5">
          <Calendar className="w-3 h-3 text-neutral-400" strokeWidth={2} />
          {formatDate(servico.data)}
        </span>
        <span className="inline-flex items-center gap-1.5">
          <Euro className="w-3 h-3 text-neutral-400" strokeWidth={2} />
          {formatEur(servico.valor)}
        </span>
        {servico.tecnico && (
          <span className="inline-flex items-center gap-1.5">
            <UserCircle2 className="w-3 h-3 text-neutral-400" strokeWidth={2} />
            {servico.tecnico}
          </span>
        )}
        {servico.created_by_name && (
          <span className="inline-flex items-center gap-1.5 text-neutral-400">
            Registado por {servico.created_by_name}
            {servico.created_by_email ? ` · ${servico.created_by_email}` : ""}
          </span>
        )}
      </div>

      {servico.attachments && servico.attachments.length > 0 && (
        <div className="mt-4 pt-4 border-t border-zinc-100">
          <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-400 font-chivo font-bold mb-2 flex items-center gap-1.5">
            <Paperclip className="w-3 h-3" strokeWidth={2} />
            Anexos ({servico.attachments.length})
          </div>
          <ul className="space-y-1.5">
            {servico.attachments.map((att) => (
              <li key={att.id} className="flex items-center justify-between gap-2 group">
                <button
                  data-testid={`download-attachment-${att.id}`}
                  onClick={() => onDownload(att)}
                  className="flex items-center gap-2 text-sm font-manrope text-neutral-700 hover:text-[#0047FF] transition-colors min-w-0"
                >
                  <Download className="w-3.5 h-3.5 shrink-0" strokeWidth={2} />
                  <span className="truncate">{att.filename}</span>
                </button>
                <span className="text-[11px] font-manrope text-neutral-400 shrink-0">
                  {formatSize(att.size)}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function ConsultaEvent({ consulta }) {
  const stateStyle =
    CONSULTA_STATE_STYLES[consulta.estado] || "bg-zinc-100 text-zinc-600 border-zinc-200";
  return (
    <div
      className="bg-white border border-zinc-200 rounded-sm p-5 hover:border-zinc-300 transition-colors"
      data-testid={`event-consulta-${consulta.id}`}
    >
      <div className="flex items-start justify-between gap-4 flex-wrap mb-3">
        <div className="flex items-center gap-2 min-w-0">
          <MessageSquareText className="w-4 h-4 text-amber-600 shrink-0" strokeWidth={2} />
          <div className="text-[10px] uppercase tracking-[0.24em] text-amber-700 font-chivo font-bold">
            Consulta
          </div>
          <div className="text-sm font-manrope font-semibold text-neutral-950 truncate">
            · {consulta.assunto}
          </div>
        </div>
        <span
          className={`inline-flex items-center px-2.5 py-1 text-[10px] uppercase tracking-[0.16em] font-chivo font-bold border rounded-sm ${stateStyle}`}
        >
          {consulta.estado}
        </span>
      </div>

      {consulta.descricao && (
        <p className="text-sm font-manrope text-neutral-700 leading-relaxed mb-3 whitespace-pre-line">
          {consulta.descricao}
        </p>
      )}

      <div className="flex flex-wrap gap-x-5 gap-y-1.5 text-xs font-manrope text-neutral-500">
        <span className="inline-flex items-center gap-1.5">
          <Calendar className="w-3 h-3 text-neutral-400" strokeWidth={2} />
          {formatDate(consulta.data)}
        </span>
        {consulta.contacto_nome && (
          <span className="inline-flex items-center gap-1.5">
            <UserCircle2 className="w-3 h-3 text-neutral-400" strokeWidth={2} />
            {consulta.contacto_nome}
          </span>
        )}
        {consulta.contacto_email && (
          <span className="inline-flex items-center gap-1.5">
            {consulta.contacto_email}
          </span>
        )}
        {consulta.contacto_telefone && (
          <span className="inline-flex items-center gap-1.5">{consulta.contacto_telefone}</span>
        )}
        {consulta.created_by_name && (
          <span className="inline-flex items-center gap-1.5 text-neutral-400">
            Registado por {consulta.created_by_name}
            {consulta.created_by_email ? ` · ${consulta.created_by_email}` : ""}
          </span>
        )}
      </div>
    </div>
  );
}
